<template>
    <skeleton-loader v-if="loading" type="stats-panel" />

    <div v-else class="mkt-panel">
        <div class="mkt-kpis">
            <kpi-card :label="$t('business.ads_investment')" :value="formatMoney(ads)"
                :hint="$t('business.ads_investment_hint')" />
            <kpi-card :label="$t('business.cac')" :value="cacLabel" :hint="$t('business.cac_hint')" />
            <kpi-card :label="$t('business.close_rate')" :value="rateLabel(closeRate)"
                :hint="$t('business.close_rate_hint')" />
        </div>

        <div class="mkt-funnel">
            <span class="mkt-funnel-title">{{ $t('business.funnel') }}</span>
            <div class="funnel-steps">
                <div class="funnel-step">
                    <span class="funnel-step-name">{{ $t('business.inquiries') }}</span>
                    <input class="funnel-step-input" type="number" min="0" :value="funnel.inquiries"
                        :placeholder="emptyMark" @change="onChangeStep('inquiries', $event)">
                </div>

                <v-icon class="funnel-arrow" size="16">mdi-arrow-right</v-icon>

                <div class="funnel-step">
                    <span class="funnel-step-name">{{ $t('business.trials') }}</span>
                    <input class="funnel-step-input funnel-step-input--trials" type="number" min="0"
                        :value="funnel.trials" :placeholder="emptyMark" @change="onChangeStep('trials', $event)">
                    <span class="funnel-step-rate">
                        {{ $t('business.inquiry_to_trial') }}: <strong>{{ rateLabel(trialRate) }}</strong>
                    </span>
                </div>

                <v-icon class="funnel-arrow" size="16">mdi-arrow-right</v-icon>

                <div class="funnel-step">
                    <span class="funnel-step-name">{{ $t('business.closes') }}</span>
                    <input class="funnel-step-input funnel-step-input--closes" type="number" min="0"
                        :value="funnel.closes" :placeholder="emptyMark" @change="onChangeStep('closes', $event)">
                    <span class="funnel-step-rate">
                        {{ $t('business.trial_to_close') }}: <strong>{{ rateLabel(closeRate) }}</strong>
                    </span>
                </div>

                <v-icon class="funnel-arrow" size="16">mdi-arrow-right</v-icon>

                <div class="funnel-step">
                    <span class="funnel-step-name">{{ $t('business.global_rate') }}</span>
                    <span class="funnel-step-value">{{ rateLabel(globalRate) }}</span>
                    <span class="funnel-step-rate">{{ $t('business.inquiry_to_close') }}</span>
                </div>
            </div>
        </div>

        <div class="mkt-chart-card">
            <div class="mkt-chart-hdr">
                <span class="chart-title">{{ $t('business.evolution') }}</span>
                <span class="mkt-hint" :class="{ visible: hintVisible }">{{ hintText }}</span>
                <div class="mkt-vars">
                    <button v-for="variable in variables" :key="variable.key" type="button" class="mkt-var"
                        :class="{ active: isActive(variable.key), disabled: isBlocked(variable.key) }"
                        :style="varStyle(variable)" @click="toggleVariable(variable.key)">
                        {{ variable.label }}
                    </button>
                </div>
            </div>

            <skeleton-loader v-if="loadingYear" type="bar-chart" />
            <div v-else-if="!hasYearData" class="mkt-empty">{{ $t('business.no_month_data') }}</div>
            <div v-else class="mkt-chart">
                <mixed-trend-chart :labels="monthLabels" :series="series" :money-formatter="formatMoney"
                    :height="240" />
            </div>
        </div>
    </div>
</template>

<script>
import SkeletonLoader from "@/components/common/SkeletonLoader";
import KpiCard from "@/components/common/KpiCard";
import MixedTrendChart from "@/components/common/MixedTrendChart";
import {
    computeCac, computeRate, defaultFunnel, formatMoney, getAdsInvestment, loadCashYear,
} from "@/utils/Business";

const MAX_VARIABLES = 3

const HINT_TIMEOUT = 1800

export default {
    name: "StatsMarketing",
    components: { SkeletonLoader, KpiCard, MixedTrendChart },
    props: {
        companyId: {
            type: String,
            default: null,
        },
        period: {
            type: String,
            default: '',
        },
        cash: {
            type: Object,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loadingYear: true,
            year: null,
            months: {},
            selected: ['closes', 'trials'],
            hintVisible: false,
            hintTimer: null,
        }
    },
    computed: {
        emptyMark(vm = this) {
            return vm.$t('business.no_value')
        },
        hintText(vm = this) {
            return vm.$t('business.max_variables', { n: MAX_VARIABLES })
        },
        funnel(vm = this) {
            return vm.cash?.funnel || defaultFunnel()
        },
        ads(vm = this) {
            return getAdsInvestment(vm.cash)
        },
        trialRate(vm = this) {
            return computeRate(vm.funnel.inquiries, vm.funnel.trials)
        },
        closeRate(vm = this) {
            return computeRate(vm.funnel.trials, vm.funnel.closes)
        },
        globalRate(vm = this) {
            return computeRate(vm.funnel.inquiries, vm.funnel.closes)
        },
        cac(vm = this) {
            return computeCac(vm.ads, vm.funnel.closes)
        },
        cacLabel(vm = this) {
            return vm.cac === null ? vm.$t('common.without_data') : formatMoney(vm.cac)
        },
        variables(vm = this) {
            return [
                { key: 'closes', label: vm.$t('business.closes'), color: '#66CC99' },
                { key: 'trials', label: vm.$t('business.trials'), color: '#66CCCC' },
                { key: 'ads', label: vm.$t('business.ads_investment'), color: '#E6AC00', money: true },
                { key: 'cac', label: vm.$t('business.cac'), color: '#E85C5C', money: true },
                { key: 'closeRate', label: vm.$t('business.close_rate_short'), color: '#CCFF33', suffix: '%' },
                { key: 'inquiries', label: vm.$t('business.inquiries'), color: '#9B6DFF' },
            ]
        },
        monthLabels(vm = this) {
            void vm.$i18n.locale
            return Array.from({ length: 12 }, (item, i) => {
                return vm.$moment().month(i).format('MMM').replace('.', '').toUpperCase()
            })
        },
        monthly(vm = this) {
            return Array.from({ length: 12 }, (item, i) => {
                const key = `${vm.year}-${String(i + 1).padStart(2, '0')}`
                const monthCash = key === vm.period ? vm.cash : vm.months[key]
                if (!monthCash) return null

                const funnel = monthCash.funnel || defaultFunnel()
                const ads = getAdsInvestment(monthCash)
                return {
                    inquiries: funnel.inquiries,
                    trials: funnel.trials,
                    closes: funnel.closes,
                    ads: ads || null,
                    cac: computeCac(ads, funnel.closes),
                    closeRate: computeRate(funnel.trials, funnel.closes),
                }
            })
        },
        hasYearData(vm = this) {
            return vm.monthly.some(month => month && Object.values(month).some(value => value !== null))
        },
        series(vm = this) {
            return vm.variables
                .filter(variable => vm.selected.includes(variable.key))
                .map(variable => ({
                    label: variable.label,
                    color: variable.color,
                    money: !!variable.money,
                    suffix: variable.suffix || '',
                    data: vm.monthly.map(month => (month ? month[variable.key] : null)),
                }))
        },
    },
    mounted() {
        this.loadYear()
    },
    beforeDestroy() {
        clearTimeout(this.hintTimer)
    },
    methods: {
        formatMoney,
        rateLabel(value) {
            return value === null ? this.emptyMark : `${value}%`
        },
        isActive(key) {
            return this.selected.includes(key)
        },
        isBlocked(key) {
            return !this.isActive(key) && this.selected.length >= MAX_VARIABLES
        },
        varStyle(variable) {
            if (!this.isActive(variable.key)) return null
            return { background: variable.color, borderColor: variable.color, color: '#0C0E12' }
        },
        toggleVariable(key) {
            const vm = this
            if (vm.isActive(key)) {
                if (vm.selected.length === 1) return
                vm.selected = vm.selected.filter(item => item !== key)
                return
            }
            if (vm.selected.length >= MAX_VARIABLES) return vm.showHint()
            vm.selected = [...vm.selected, key]
        },
        showHint() {
            const vm = this
            vm.hintVisible = true
            clearTimeout(vm.hintTimer)
            vm.hintTimer = setTimeout(() => { vm.hintVisible = false }, HINT_TIMEOUT)
        },
        onChangeStep(step, event) {
            const vm = this
            const raw = event.target.value
            const value = raw === '' ? null : Math.max(0, Math.round(Number(raw) || 0))
            event.target.value = value === null ? '' : value
            if (value === vm.funnel[step]) return
            vm.$emit('change:funnel', { ...vm.funnel, [step]: value })
        },
        async loadYear() {
            const vm = this
            const year = Number(String(vm.period).slice(0, 4))
            if (!vm.companyId || !year) return
            vm.loadingYear = true
            vm.year = year
            try {
                vm.months = await loadCashYear(vm.companyId, year)
            } catch (error) {
                vm.months = {}
            } finally {
                vm.loadingYear = false
            }
        },
    },
    watch: {
        period(value, previous) {
            if (String(value).slice(0, 4) !== String(previous).slice(0, 4)) this.loadYear()
        },
    },
}
</script>

<style scoped>
.mkt-panel {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.mkt-kpis {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
}

.mkt-funnel {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
    padding: 14px 16px;
}

.mkt-funnel-title {
    display: block;
    margin-bottom: 12px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--v-txtMuted-base);
}

.funnel-steps {
    display: flex;
    align-items: flex-start;
    gap: 8px;
}

.funnel-step {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    text-align: center;
}

.funnel-step-name {
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
}

.funnel-step-input {
    width: 100%;
    max-width: 110px;
    height: 34px;
    border-radius: 8px;
    background: var(--v-field-base);
    border: 1px solid transparent;
    text-align: center;
    font-size: 20px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    outline: none;
    -moz-appearance: textfield;
    transition: border-color 0.15s;
}

.funnel-step-input:hover {
    border-color: var(--v-outline-base);
}

.funnel-step-input:focus {
    border-color: var(--v-accent-base);
}

.funnel-step-input--trials {
    color: var(--v-secondary-base);
}

.funnel-step-input--closes {
    color: var(--v-success-base);
}

.funnel-step-input::-webkit-outer-spin-button,
.funnel-step-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.funnel-step-value {
    height: 34px;
    display: flex;
    align-items: center;
    font-size: 20px;
    font-weight: 700;
    color: #9B6DFF;
}

.funnel-step-rate {
    font-size: 10px;
    color: var(--v-txtMuted-base);
}

.funnel-arrow {
    margin-top: 22px;
    color: var(--v-txtMuted-base) !important;
    flex-shrink: 0;
}

.mkt-chart-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.mkt-chart-hdr {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.chart-title {
    font-size: var(--fs-caption);
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.mkt-vars {
    display: flex;
    align-items: center;
    gap: 5px;
    flex-wrap: wrap;
    margin-left: auto;
}

.mkt-var {
    padding: 4px 10px;
    border-radius: 7px;
    border: 1px solid var(--v-outline-base);
    background: transparent;
    font-size: 10px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
    transition: background 0.15s, color 0.15s, border-color 0.15s, opacity 0.15s;
}

.mkt-var:hover {
    color: var(--v-txtPrimary-base);
}

.mkt-var.disabled {
    opacity: 0.35;
    cursor: default;
}

.mkt-hint {
    white-space: nowrap;
    font-size: 10px;
    font-weight: 600;
    color: var(--v-error-base);
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.15s;
}

.mkt-hint.visible {
    visibility: visible;
    opacity: 1;
}

.mkt-chart {
    height: 240px;
}

.mkt-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 120px;
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}

@media (max-width: 960px) {
    .mkt-kpis {
        grid-template-columns: 1fr;
    }

    .funnel-steps {
        flex-wrap: wrap;
    }

    .funnel-step {
        flex: 1 1 40%;
    }

    .funnel-arrow {
        display: none;
    }
}
</style>
